import { useContext, useState } from 'react';
import { Box, Link, Text } from '@bitrise/bitkit';

import PublicPage from '@/components/page/PublicPage';
import PageContent from '@/components/page/PageContent/PageContent';
import PageTitle, { PageTitleHeading, PageTitleSubheading } from '@/components/page/PageTitle/PageTitle';
import NotificationSlot from '@/components/NotificationSlot/NotificationSlot';
import LoginCard from '@/components/LoginCard/LoginCard';
import BackButton from '@/components/buttons/BackButton/BackButton';
import LoginButtonGroup from '@/components/buttons/LoginButtonGroup/LoginButtonGroup';
import StaticHeader from '@/components/page/PageHeader/StaticHeader';
import useQueryParams from '@/hooks/useQueryParams';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import SSOEmailForm from '@/pages/SignIn/SignInPage/SSOEmailForm';
import useSignUpFlashMessage from '../hooks/useSignUpFlashMessage';
import SignUpForm from './components/SignUpForm';

const SignUpPage = () => {
  const queryParams: URLSearchParams = useQueryParams();
  const prefillEmail = queryParams.get('email') || undefined;
  const forcePrefilled = queryParams.has('force_email');

  useSignUpFlashMessage();

  const [formState, setFormState] = useState<'selection' | 'email' | 'sso'>(prefillEmail ? 'email' : 'selection');

  const { featureFlags } = useContext(GlobalPropsContext);
  const ssoButtonProps = featureFlags.user.emailSamlSignIn
    ? {
        onClick: () => setFormState('sso'),
      }
    : {
        as: 'a' as const,
        href: `/users/sign-in/saml`,
      };

  return (
    <PublicPage
      bgGradient="linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, purple.95 100%), neutral.100"
      h="calc(100vh)"
    >
      <StaticHeader>
        <Link colorScheme="purple" href="/users/sign_in">
          Log in &rarr;
        </Link>
      </StaticHeader>
      <PageTitle textAlign="center">
        <PageTitleHeading size="6">Sign up, start building</PageTitleHeading>
        <PageTitleSubheading size="3">
          Start building on the only true mobile-first DevOps platform.
        </PageTitleSubheading>
      </PageTitle>
      <PageContent paddingTop="0">
        <NotificationSlot />
        {formState !== 'sso' && (
          <LoginCard>
            {formState === 'email' && (
              <Box display="flex" flexDirection="column" gap="24" maxWidth="368">
                <BackButton onBack={() => setFormState('selection')} text="Back to all sign up methods" />
                <Text as="h2" fontWeight="bold" size="4">
                  Sign up with Email
                </Text>
                <SignUpForm prefillEmail={prefillEmail} forcePrefilled={forcePrefilled} />
              </Box>
            )}
            {formState === 'selection' && (
              <LoginButtonGroup
                ssoButtonProps={ssoButtonProps}
                onEmailSelect={() => setFormState('email')}
                signUpMode
              />
            )}
          </LoginCard>
        )}
        {formState === 'sso' && (
          <SSOEmailForm
            requireConsent
            backLabel="Sign up without SSO"
            cardProps={{ gap: '32' }}
            header={<Text textStyle="heading/h3">Sign up with SSO</Text>}
            onBack={() => setFormState('selection')}
          />
        )}
      </PageContent>
    </PublicPage>
  );
};

export default SignUpPage;
